import * as Sentry from '@sentry/react';
import { getSession } from 'next-auth/react';
import axios from 'axios';

import axiosInstance from './axiosInterceptor';
import ApiException from '@exceptions/ApiExceptions';
import { eventEmitter } from '@lib/events';
import { prepareAuditLogData } from '@utils/auditLog';

import { ServiceContext } from '@interfaces/serviceContext';
import { AuditOperation } from '@interfaces/auditLog';
import { ISession } from '@interfaces/session';

type Method = 'get' | 'post' | 'put' | 'delete';
type Headers = { [key: string]: string };

type SearchObj = {
  [key: string]: string | number | boolean;
};

type CreateOptions = {
  requestQuery?: string;
};

export class BaseAPI {
  headers: Headers;
  endpointUrl: string;
  tenantId?: string;
  token?: string;
  noInterception?: boolean;
  serviceContext?: ServiceContext;

  constructor(serviceContext?: ServiceContext) {
    this.tenantId = serviceContext?.tenantId;
    this.token = serviceContext?.token;
    this.endpointUrl = '';
    this.headers = { 'content-type': 'application/json' };
    this.noInterception = false;
    this.serviceContext = serviceContext;
  }

  _setRequestHeaders = async () => {
    const _headers = this.headers;
    if (this.token) {
      _headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (!this.tenantId) {
      const session = await getSession() as unknown as ISession['data'];
      this.tenantId = session?.tenantId;
    }

    // _headers['x-tenant-id'] = this.tenantId;
    return _headers;
  };

  _getAxiosInstance() {
    return this.noInterception ? axios : axiosInstance;
  }

  async _apiCall(method: Method, query = '', data?: unknown) {
    const url = this.endpointUrl + query;

    try {
      const requestClient = this._getAxiosInstance();
      if (method === 'get') {
        const resp = await requestClient.get(url, {
          headers: await this._setRequestHeaders(),
        });

        return resp.data;
      }
      if (method === 'post') {
        const resp = await requestClient.post(url, data, {
          headers: await this._setRequestHeaders(),
        });

        this._logAudit(AuditOperation.CREATE);
        return resp.data;
      }
      if (method === 'put') {
        const resp = await requestClient.put(url, data, {
          headers: await this._setRequestHeaders(),
        });

        this._logAudit(AuditOperation.UPDATE);
        return resp.data;
      }
      if (method === 'delete') {
        const resp = await requestClient.delete(url, data, {
          headers: await this._setRequestHeaders(),
        });

        this._logAudit(AuditOperation.DELETE);
        return resp.data;
      }
    } catch (error: any) {
      const statusCode = error.response?.status || undefined;
      const errorMessage =
        error.response?.data?.message || 'An unexpected error occurred';
      Sentry.captureException(error);
      throw new ApiException(statusCode, errorMessage);
    }
  }

  async get(arg?: number | string) {
    if (arg) {
      if (!Number.isNaN(Number(arg))) {
        arg = `/${arg}`;
      }
      return await this._apiCall('get', arg as string);
    }
    return await this._apiCall('get');
  }

  async search(searchObj: SearchObj) {
    let requestQuery = '?';
    Object.keys(searchObj).forEach((key: string, i: number) => {
      requestQuery += `${key}=${searchObj[key]}${
        i < Object.keys(searchObj).length - 1 ? '&' : ''
      }`;
    });
    return await this.get(requestQuery);
  }

  async create(data: unknown, options?: CreateOptions) {
    return await this._apiCall('post', options?.requestQuery, data);
  }

  async update(data: unknown, arg?: number | string, passIdAsParam = false) {
    if (arg && !Number.isNaN(Number(arg))) {
      if (passIdAsParam) {
        arg = `?id=${arg}`;
      } else {
        arg = `/${arg}`;
      }
    }

    return await this._apiCall('put', arg as string | undefined, data);
  }

  async delete(id: number | string, passIdAsParam = false) {
    const requestQuery = passIdAsParam ? `?id=${id}` : `/${id}`;
    return await this._apiCall('delete', requestQuery);
  }

  private async _logAudit(operationType: AuditOperation) {
    if (this.serviceContext?.resourceName) {
      this.serviceContext['operation'] = operationType;
      await axios.post(
        '/api/audit-log',
        await prepareAuditLogData(this.serviceContext),
      );
      eventEmitter.emit('refetch-audit-log');
    }
  }
}
